import { apiClient, API_BASE } from '../utils/api';

export interface IngredientPayload {
  inci_name: string;
  cas_number?: string | null;
  function?: string | null;
  noael_mg_kg_day?: number | null;
  dermal_absorption_pct?: number | null;
  sccs_ref?: string | null;
  allergenicity?: string | null;
  toxicological_class?: string | null;
  metadata_json?: string | null;
}

export interface FormulaItemIn {
  ingredient_id?: number | null;
  ingredient_name: string;
  inci_name?: string | null;
  cas_number?: string | null;
  concentration_pct: number;
  function?: string | null;
  override_da_pct?: number | null;
  override_noael_mg_kg_day?: number | null;
}

export const traApi = {
  listCategories: () => apiClient.get('/tra/categories'),
  searchIngredients: (query?: string) => apiClient.get(`/tra/ingredients${query ? `?query=${encodeURIComponent(query)}` : ''}`),
  getIngredients: () => apiClient.get('/tra/ingredients'),
  createIngredient: (payload: IngredientPayload) => apiClient.post('/tra/ingredients', payload),
  getProducts: () => apiClient.get('/products/?limit=200&sort_by=name&sort_order=asc'),
  getFormulas: () => apiClient.get('/tra/formulas').catch(() => ({ data: [] })), // Fallback if endpoint doesn't exist
  getAssessments: () => apiClient.get('/tra/assessments'),
  createFormula: (payload: { name: string; product_id?: number | null; items: FormulaItemIn[] }) => 
    apiClient.post('/tra/formulas', payload),
  createAssessment: (formulaId: number, productCategoryCode: string = 'LEAVE_ON_FACE', notes: string = 'New assessment') => 
    apiClient.post('/tra/assessments', { formula_id: formulaId, product_category_code: productCategoryCode, notes }),
  calculateAssessment: (assessmentId: number) => apiClient.post(`/tra/assessments/${assessmentId}/calculate`, {}),
  listFormulaItems: (formulaId: number) => apiClient.get(`/tra/formulas/${formulaId}/items`),
  uploadFormulaCsv: async (formulaId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.upload(`/tra/formulas/${formulaId}/upload-csv`, fd);
  },
  uploadFormulaXlsx: async (formulaId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.upload(`/tra/formulas/${formulaId}/upload-xlsx`, fd);
  },
  listResults: (assessmentId: number) => apiClient.get(`/tra/assessments/${assessmentId}/results`),
  submitReview: (assessmentId: number, comments?: string) => apiClient.post(`/tra/assessments/${assessmentId}/submit-review`, { comments }),
  approveAssessment: (assessmentId: number, comments?: string) => apiClient.post(`/tra/assessments/${assessmentId}/approve`, { comments }),
  rejectAssessment: (assessmentId: number, comments?: string) => apiClient.post(`/tra/assessments/${assessmentId}/reject`, { comments }),
  attachReviewPdf: async (assessmentId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.upload(`/tra/assessments/${assessmentId}/reviews/attach-pdf`, fd);
  },
  createAmendment: (assessmentId: number, data: { formula_item_id: number; field: 'override_da_pct' | 'override_noael_mg_kg_day'; new_value?: number | null; justification: string; attachment_document_id?: number | null; }) =>
    apiClient.post(`/tra/assessments/${assessmentId}/amendments`, data),
  listAmendments: (assessmentId: number) => apiClient.get(`/tra/assessments/${assessmentId}/amendments`),
  openReport: async (assessmentId: number) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/tra/assessments/${assessmentId}/report`, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) throw new Error('Failed to fetch report');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  },
  exportXlsx: async (assessmentId: number) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/tra/assessments/${assessmentId}/export/xlsx`, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) throw new Error('Failed to export XLSX');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tra_assessment_${assessmentId}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
  enrichPubChem: (ingredientId: number, casNumber: string) =>
    apiClient.post(`/tra/reference/pubchem/enrich?ingredient_id=${encodeURIComponent(String(ingredientId))}&cas_number=${encodeURIComponent(casNumber)}`, {}),
  uploadOpenFoodTox: async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.upload(`/tra/reference/openfoodtox/upload`, fd);
  },
  uploadCosIng: async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.upload(`/tra/reference/cosing/upload`, fd);
  },
  uploadECHA: async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.upload(`/tra/reference/echa/upload`, fd);
  },
  getDashboardSummary: () => apiClient.get('/tra/dashboard/summary'),
  listAssessments: (filters?: { status?: string; mos_status?: string; reviewer_id?: number; product_id?: number; search?: string; limit?: number; offset?: number; }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.mos_status) params.set('mos_status', filters.mos_status);
    if (filters?.reviewer_id != null) params.set('reviewer_id', String(filters.reviewer_id));
    if (filters?.product_id != null) params.set('product_id', String(filters.product_id));
    if (filters?.search) params.set('search', filters.search);
    if (filters?.limit != null) params.set('limit', String(filters.limit));
    if (filters?.offset != null) params.set('offset', String(filters.offset));
    const qs = params.toString();
    return apiClient.get(`/tra/assessments${qs ? `?${qs}` : ''}`);
  },
  listProducts: (limit = 200) => apiClient.get(`/products/?limit=${limit}&sort_by=name&sort_order=asc`),
  listFacilities: (limit = 200) => apiClient.get(`/facilities/?limit=${limit}`),
  createFacility: (name: string) => apiClient.post('/facilities/', { name }),
  createProduct: (payload: { name: string; product_type?: string; intended_use?: string; target_audience?: string; safety_data_available?: boolean; ingredients?: string }) => {
    // Get first facility for now - in real app, user would select
    return apiClient.get('/facilities/?limit=1').then(facilitiesRes => {
      const facilityId = (facilitiesRes.data as any)?.[0]?.id || 1;
      return apiClient.post('/products/', {
        name: payload.name,
        facility_id: facilityId,
        product_type: payload.product_type || 'cosmetic',
        intended_use: payload.intended_use || 'General cosmetic use',
        target_audience: payload.target_audience || 'Adults',
        safety_data_available: payload.safety_data_available !== false,
        ingredients: payload.ingredients || ''
      });
    });
  },
  importFormulaFromProduct: (productId: number, name?: string) => apiClient.post(`/tra/products/${productId}/import-formula${name ? `?name=${encodeURIComponent(name)}` : ''}`, {}),
  triggerReassessment: (productId: number, reason?: string) => apiClient.post(`/tra/products/${productId}/assessments/reassess${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`, {}),
};

export default traApi;
